import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../App';
import { Calendar, MapPin, Clock, Radio, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import LiveStreamViewer from '../../components/LiveStreamViewer';

export default function PawsBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const [watchingBookingId, setWatchingBookingId] = useState(null);

  useEffect(() => {
    apiFetch('/api/bookings').then(b => { setBookings(b); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const upcoming = bookings.filter(b => ['CONFIRMED', 'REQUESTED', 'IN_PROGRESS'].includes(b.status));
  const past = bookings.filter(b => ['COMPLETED'].includes(b.status));
  const cancelled = bookings.filter(b => ['CANCELLED', 'NO_SHOW'].includes(b.status));
  const displayed = tab === 'upcoming' ? upcoming : tab === 'past' ? past : cancelled;

  return (
    <div data-testid="paws-bookings" className="max-w-4xl">
      <h1 className="font-outfit text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>My Bookings</h1>
      
      <div className="flex gap-2 mb-6">
        {[
          { key: 'upcoming', label: `Upcoming (${upcoming.length})` },
          { key: 'past', label: `Past (${past.length})` },
          { key: 'cancelled', label: `Cancelled (${cancelled.length})` },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === t.key ? 'bg-petfolk-orange text-white' : ''}`}
            style={{ color: tab !== t.key ? 'var(--text-secondary)' : undefined }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="skeleton h-28 rounded-2xl" />)}</div>
      ) : displayed.length === 0 ? (
        <div className="petfolk-card p-10 text-center">
          <p style={{ color: 'var(--text-secondary)' }}>No {tab} bookings</p>
        </div>
      ) : (
        <div className="space-y-4">
          {displayed.map(b => (
            <div key={b.id} className="petfolk-card p-5" data-testid={`booking-${b.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-petfolk-orange/20 to-petfolk-purple/10 flex items-center justify-center text-2xl shrink-0">
                  {b.pet?.species === 'DOG' ? '🐕' : '🐈'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{b.service?.name}</h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>for {b.pet?.name}</p>
                    </div>
                    <span className={`badge badge-${b.status.toLowerCase().replace('_', '-')}`}>{b.status.replace('_', ' ')}</span>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    <span className="flex items-center gap-1"><MapPin size={12} /> {b.node?.displayName}</span>
                    {b.scheduledStart && <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(b.scheduledStart).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                    {b.scheduledStart && <span className="flex items-center gap-1"><Clock size={12} /> {new Date(b.scheduledStart).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
                    <div className="flex gap-2 flex-wrap items-center">
                      <span className={`badge badge-${b.paymentStatus?.toLowerCase()}`}>{b.paymentStatus}</span>
                      {b.liveStreamEnabled && (
                        <span className="badge" style={{ background: 'rgba(139,92,246,0.1)', color: '#8B5CF6' }}>
                          🎥 Live Grooming Add-on
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>₹{b.netAmount?.toLocaleString()}</span>
                      <Link to={`/paws/bookings/${b.id}`} className="flex items-center gap-1 text-xs font-semibold hover:opacity-70 transition-opacity" style={{ color: 'var(--petfolk-orange)' }}>
                        <ExternalLink size={12} /> Track
                      </Link>
                    </div>
                  </div>

                  {/* Live Stream Watch Button — only when stream is LIVE */}
                  {b.liveStreamEnabled && b.liveStreamStatus === 'LIVE' && watchingBookingId !== b.id && (
                    <button
                      onClick={() => setWatchingBookingId(b.id)}
                      className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 animate-pulse"
                      style={{ background: 'linear-gradient(135deg, #EF4444, #8B5CF6)' }}
                      data-testid={`watch-live-${b.id}`}
                    >
                      <Radio size={14} />
                      🔴 Watch Live Grooming Session
                    </button>
                  )}

                  {/* Live Stream Player */}
                  {watchingBookingId === b.id && (
                    <div className="mt-3">
                      <LiveStreamViewer
                        booking={b}
                        onClose={() => setWatchingBookingId(null)}
                      />
                    </div>
                  )}

                  {/* Waiting indicator — live stream enabled but not started yet */}
                  {b.liveStreamEnabled && b.liveStreamStatus === 'NOT_REQUESTED' && tab === 'upcoming' && (
                    <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-xl text-xs" style={{ background: 'rgba(139,92,246,0.08)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.15)' }}>
                      <Radio size={12} />
                      Live Grooming session will be available when your groomer starts streaming
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
